import React, {
  useCallback,
  useMemo,
  useRef,
  useState,
  useEffect,
} from 'react';
import { MenuTabs } from 'components/menus/MenuTabs';
import { Tab } from 'components/menus/Tab';
import ButtonNew from './inputs/ButtonNew';
import TextField from 'components/inputs/TextField';
import ImageField from './inputs/ImageField';
import TextArea from './inputs/TextArea';
import NumberField from './inputs/NumberField';
import ButtonSet from './inputs/ButtonSet';
import Dropdown from './inputs/Dropdown';
import MultiRange from './inputs/MultiRange';
import SimpleToggle from './inputs/SimpleToggle';
import './SettingsNew.scss';
import classNames from 'classnames';
import MiniLoader from './MiniLoader';

const SettingsControlLabel = ({ label }) => {
  return <div className="SettingsNew-label">{label}</div>;
};

const SettingsControlDescription = ({ description }) => {
  return <div className="SettingsNew-description">{description}</div>;
};

const SettingsToggle = ({ label, disabled, onChange, value }) => {
  return (
    <div className="SettingsToggle SettingsNew-column">
      {label ? <SettingsControlLabel label={label} /> : null}
      <SimpleToggle onChange={onChange} value={value} disabled={disabled} />
    </div>
  );
};

// TODO come back through and update values and labels
// Confirmed all options passed here appear valid
const SettingsDropdown = ({
  disabled,
  label,
  onChange,
  options,
  value,
  description,
  isStatic,
  staticPlaceholder,
  ...restProps
}) => {
  const valueLabel = options.find(o => o.value === value)?.label;

  return (
    <div className="SettingsDropdown SettingsNew-column">
      {label ? <SettingsControlLabel label={label} /> : null}
      {description ? (
        <SettingsControlDescription description={description} />
      ) : null}

      {isStatic ? (
        staticPlaceholder ? (
          staticPlaceholder
        ) : (
          <div className="SettingsNumberField-static">{value}</div>
        )
      ) : (
        <div className="SettingsDropdown-container">
          <Dropdown
            disabled={disabled}
            onChange={onChange}
            options={options}
            value={valueLabel}
            menuPortalTarget={document.body}
            {...restProps}
          />
        </div>
      )}
    </div>
  );
};

const SettingsButtonSet = ({
  label,
  disabled,
  onChange,
  options,
  value,
  description,
  isStatic,
  staticPlaceholder,
}) => {
  return (
    <div className="SettingsButtonSet SettingsNew-column">
      {label ? <SettingsControlLabel label={label} /> : null}
      {description ? (
        <SettingsControlDescription description={description} />
      ) : null}

      {isStatic ? (
        staticPlaceholder ? (
          staticPlaceholder
        ) : (
          <div className="SettingsNumberField-static">
            {options.find(o => o.value === value)?.label}
          </div>
        )
      ) : (
        <div className="SettingsButtonSet-container">
          <ButtonSet
            onChange={onChange}
            value={value}
            options={options}
            disabled={disabled}
          />
        </div>
      )}
    </div>
  );
};

const SettingsDescription = ({
  label,
  description,
  onChange,
  placeholder,
  value,
  validationMessage,
  isStatic,
}) => {
  if (isStatic && !value) return null;
  return (
    <div className="SettingsDescription SettingsNew-column">
      {label ? <SettingsControlLabel label={label} /> : null}
      {description ? (
        <SettingsControlDescription description={description} />
      ) : null}
      {isStatic ? (
        <div className="SettingsDescription-static">{value}</div>
      ) : (
        <div className="SettingsDescription-text-container">
          <TextArea
            onChange={onChange}
            value={value}
            validationMessage={validationMessage}
            placeholder={placeholder}
          />
        </div>
      )}
    </div>
  );
};

const SettingsViewOnlyText = ({ label, text }) => {
  return (
    <div className="SettingsViewOnlyText SettingsNew-column">
      <div className="SettingsNew-label">{label}</div>
      <div className="SettingsViewOnlyText-text">{text}</div>
    </div>
  );
};

const SettingsTextField = ({
  label,
  onChange,
  placeholder,
  validationMessage,
  value,
  description,
  isStatic,
}) => {
  if (isStatic && !value) return null;
  return (
    <div className="SettingsTextField SettingsNew-column">
      <>
        {label ? <SettingsControlLabel label={label} /> : null}
        {description ? (
          <SettingsControlDescription description={description} />
        ) : null}
        {isStatic ? (
          <div className="SettingsTextField-static">{value}</div>
        ) : (
          <TextField
            onChange={onChange}
            placeholder={placeholder}
            validationMessage={validationMessage}
            value={value}
            className="SettingsTextField-input"
          />
        )}
      </>
    </div>
  );
};

const SettingsTextFieldConfirm = ({
  label,
  onChange,
  placeholder,
  disabled,
  isLoading,
  onConfirm,
  validationMessage,
  value,
  description,
  isStatic,
}) => {
  return (
    <div className="SettingsTextFieldConfirm SettingsNew-column">
      {label ? <SettingsControlLabel label={label} /> : null}
      {isStatic ? (
        <div className="SettingsTextField-static">{value}</div>
      ) : (
        <div className="SettingsTextFieldConfirm-button-container">
          <TextField
            onChange={onChange}
            placeholder={placeholder}
            validationMessage={validationMessage}
            value={value}
            className="SettingsTextFieldConfirm-input"
          />
          {isLoading ? (
            <div className="SettingsTextFieldConfirm-loader-container">
              <MiniLoader />
            </div>
          ) : (
            <ButtonNew
              label="Ok"
              buttonClasses={classNames('secondary-button-small', { disabled })}
              onClick={onConfirm}
            />
          )}
        </div>
      )}
      {description ? (
        <SettingsControlDescription description={description} />
      ) : null}
    </div>
  );
};

const SettingsNumberField = ({
  label,
  onChange,
  validationMessage,
  value,
  step,
  description,
  isStatic,
  min,
  max,
  ...restProps
}) => {
  const [localValue, setLocalValue] = useState(value);
  const focused = useRef(false);

  useEffect(() => {
    if (!focused.current) {
      setLocalValue(value);
    }
  }, [value]);

  return (
    <div className="SettingsNumberField SettingsNew-column">
      {label ? <SettingsControlLabel label={label} /> : null}
      {description ? (
        <SettingsControlDescription description={description} />
      ) : null}
      {isStatic ? (
        <div className="SettingsNumberField-static">{value}</div>
      ) : (
        <NumberField
          onChange={setLocalValue}
          onBlur={v => {
            focused.current = false;
            let clamped = Number(v);
            if (min != null && clamped < Number(min)) clamped = Number(min);
            if (max != null && clamped > Number(max)) clamped = Number(max);
            setLocalValue(clamped);
            onChange(clamped);
          }}
          onFocus={() => {
            focused.current = true;
          }}
          validationMessage={validationMessage}
          step={step ?? '0.001'}
          value={localValue}
          min={min}
          max={max}
          className="SettingsNumberField-input"
          {...restProps}
        />
      )}
    </div>
  );
};

const SettingsImage = ({
  disabled,
  onChange,
  value,
  label,
  isStatic,
  ...restProps
}) => {
  return (
    <div className="SettingsImage SettingsNew-column">
      {label ? <SettingsControlLabel label={label} /> : null}
      <ImageField
        disabled={disabled}
        onChange={onChange}
        value={value}
        maxHeight={'100%'}
        maxWidth={'100%'}
        hideControls={isStatic}
        {...restProps}
      />
    </div>
  );
};

const SettingsMultiRange = ({
  label,
  disabled,
  value,
  min,
  max,
  step,
  onChange,
  showValues,
}) => {
  return (
    <div className="SettingsMultiRange SettingsNew-column">
      {label ? <SettingsControlLabel label={label} /> : null}
      <MultiRange
        disabled={disabled}
        value={value}
        onChange={onChange}
        min={min}
        max={max}
        step={step}
        showValues={showValues !== undefined ? showValues : true}
      />
    </div>
  );
};

const Settings = ({ menuOptions, activeMenuOption, footer }) => {
  const content = useMemo(
    () => menuOptions.find(o => o.value === activeMenuOption)?.content,
    [activeMenuOption, menuOptions]
  );

  return (
    <div className="SettingsNew">
      <div className="SettingsNew-content">{content}</div>

      {footer ? <div className="SettingsNew-footer">{footer}</div> : null}
    </div>
  );
};

export {
  Settings,
  SettingsDescription,
  SettingsViewOnlyText,
  SettingsTextField,
  SettingsTextFieldConfirm,
  SettingsImage,
  SettingsButtonSet,
  SettingsNumberField,
  SettingsDropdown,
  SettingsControlDescription,
  SettingsControlLabel,
  SettingsMultiRange,
  SettingsToggle,
};
